import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AccountReconciliationReason,
  AccountReconciliationStatus,
  AccountType,
  TransactionType,
  businessDate,
  type AccountReconciliation,
  type AccountWithBuckets,
  type Bucket,
  type SavingsGoalView,
} from '@financial/shared';
import {
  useAccountReconciliations,
  useAccounts,
  useCreateAccountReconciliation,
  useCreateAccount,
  useCreateBucket,
  useDeleteAccount,
  useDeleteBucket,
  useNetWorth,
  useUpdateAccount,
  useUpdateBucket,
  useUndoAccountReconciliation,
} from '../features/accounts/useAccounts';
import { ACCOUNT_TYPE_META, ACCOUNT_TYPE_ORDER } from '../lib/accountTypes';
import { currentCurrency, formatCalendarDate, formatMoney, toDateInputValue } from '../lib/format';
import { getApiErrorMessage } from '../lib/errors';
import { useSavings } from '../features/savings/useSavings';
import { useCreateTransfer } from '../features/transfers/useTransfers';
import {
  QuickAddTransactionForm,
  type QuickAddTransactionInitialValues,
} from '../features/transactions/QuickAddTransaction';
import {
  TransferBalancePreview,
  TransferForm,
  type TransferFormInitialValues,
} from '../features/transfers/TransferForm';
import { resolveBalanceTarget, transferBalanceChanges } from '../lib/balancePreview';
import { Field } from '../components/ui/Field';
import { DatePicker } from '../components/ui/DatePicker';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ItemActionsMenu } from '../components/ui/ItemActionsMenu';
import { Modal } from '../components/ui/Modal';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { Button, EmptyState, HelperText, Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { Tooltip } from '../components/ui/Tooltip';

function createBucketAttemptKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  const values = new Uint32Array(4);
  globalThis.crypto?.getRandomValues?.(values);
  const randomPart = Array.from(values, (value) => value.toString(36)).join('-');
  return `bucket-${Date.now().toString(36)}-${randomPart || Math.random().toString(36).slice(2)}`;
}

function createReconciliationAttemptKey(scope = 'neutral'): string {
  const random =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `reconciliation-${scope}-${random}`;
}

function reconciliationToday(): string {
  return businessDate(new Date()).toISOString().slice(0, 10);
}

type ReconciliationDraft = {
  balance: string;
  date: string;
  realAttempt?: {
    fingerprint: string;
    key: string;
  };
};

type ReconciliationResolution = 'income' | 'expense' | 'transfer' | 'neutral';

const MAX_SAFE_MONEY = 90_000_000_000_000;

function hasAtMostTwoMoneyDecimals(value: number): boolean {
  const normalized = Math.round(value * 100) / 100;
  return Math.abs(value - normalized) <= 1e-8;
}

const RECONCILIATION_REASON_OPTIONS = [
  {
    value: AccountReconciliationReason.BANK_RECONCILIATION,
    label: 'Comparación con banco o institución',
  },
  {
    value: AccountReconciliationReason.STATEMENT_CLOSING_BALANCE,
    label: 'Comparación con estado de cuenta',
  },
  { value: AccountReconciliationReason.OPENING_BALANCE, label: 'Saldo inicial' },
  { value: AccountReconciliationReason.CORRECTION, label: 'Corrección de captura' },
  { value: AccountReconciliationReason.OTHER, label: 'Otro motivo' },
] as const;

const RECONCILIATION_REASON_LABEL = new Map<AccountReconciliationReason, string>(
  RECONCILIATION_REASON_OPTIONS.map((option) => [option.value, option.label]),
);

function isValidCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day, 12));
  return parsed.toISOString().slice(0, 10) === value;
}

function signedMoney(amount: number, currency: AccountWithBuckets['currency']): string {
  if (amount > 0.005) return `+${formatMoney(amount, currency)}`;
  return formatMoney(amount, currency);
}

export function AccountsPage() {
  const accountsQ = useAccounts();
  const netWorthQ = useNetWorth();
  const savingsQ = useSavings();
  const [searchParams, setSearchParams] = useSearchParams();
  const newAccountOpen = searchParams.get('nuevo') === '1';

  const openNewAccount = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('nuevo', '1');
      return next;
    });
  };

  const closeNewAccount = () => {
    if (!newAccountOpen) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('nuevo');
        return next;
      },
      { replace: true },
    );
  };

  const accounts = accountsQ.data ?? [];
  const byType = netWorthQ.data?.byType ?? [];
  const donutData = byType.map((t) => ({
    name: ACCOUNT_TYPE_META[t.type].label,
    value: t.total,
    color: ACCOUNT_TYPE_META[t.type].color,
  }));
  const net = netWorthQ.data?.total ?? 0;
  const assets = netWorthQ.data?.assets ?? 0;
  const liabilities = netWorthQ.data?.liabilities ?? 0;
  const debts = netWorthQ.data?.debts ?? [];
  const goals = savingsQ.data ?? [];
  const receivables = netWorthQ.data?.receivables ?? [];
  const accountAssets = donutData.reduce((sum, item) => sum + item.value, 0);
  let cursor = 0;
  // El anillo reparte sólo los saldos de cuenta. Los préstamos por cobrar se
  // muestran aparte porque son patrimonio, pero no dinero líquido.
  const wealthGradient = donutData.length
    ? `conic-gradient(${donutData
        .map((item) => {
          const start = cursor;
          cursor += accountAssets ? (item.value / accountAssets) * 100 : 0;
          return `${item.color} ${start}% ${cursor}%`;
        })
        .join(', ')})`
    : '#334155';

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="accounts"
        title="Cuentas"
        description={`${accounts.length} cuentas · ${accounts.reduce((sum, account) => sum + account.buckets.length, 0)} apartados. El saldo total incluye el dinero disponible y el que separaste en apartados.`}
        actions={
          <Tooltip content="Agrega una cuenta con el saldo que tiene hoy" side="bottom" align="end">
            <Button onClick={openNewAccount}>
              <span className="text-lg font-normal leading-none" aria-hidden>
                +
              </span>{' '}
              Cuenta
            </Button>
          </Tooltip>
        }
      />

      <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="order-2 min-w-0 space-y-[18px] lg:order-1">
          {accountsQ.isLoading && (
            <SkeletonState label="Cargando cuentas…" className="space-y-3">
              <Skeleton className="h-36" />
              <Skeleton className="h-36" />
            </SkeletonState>
          )}
          {accounts.length === 0 && !accountsQ.isLoading && (
            <EmptyState
              title="Aún no tienes cuentas"
              description="Crea una para saber cuánto dinero tienes y cuánto puedes usar hoy."
            />
          )}
          {Array.from(new Set(accounts.map((account) => account.currency))).map((currency) => (
            <section key={currency} className="min-w-0 space-y-[18px]">
              <h2 className="text-sm font-bold text-slate-700">Cuentas en {currency}</h2>
              {ACCOUNT_TYPE_ORDER.filter((type) =>
                accounts.some((a) => a.type === type && a.currency === currency),
              ).map((type) => {
                const typeAccounts = accounts.filter(
                  (account) => account.type === type && account.currency === currency,
                );
                const typeTotal = typeAccounts.reduce((sum, account) => sum + account.balance, 0);
                return (
                  <section key={type} className="min-w-0">
                    <div className="mb-2.5 flex items-center gap-2 text-slate-500">
                      <span
                        className="text-[15px]"
                        style={{ color: ACCOUNT_TYPE_META[type].color }}
                        aria-hidden
                      >
                        {ACCOUNT_TYPE_META[type].icon}
                      </span>
                      <h2 className="text-xs font-bold uppercase tracking-[.07em]">
                        {ACCOUNT_TYPE_META[type].label}
                      </h2>
                      <span className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs font-semibold tabular-nums">
                        {formatMoney(typeTotal, currency)}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {typeAccounts.map((account) => (
                        <AccountCard
                          key={account.id}
                          account={account}
                          accounts={accounts}
                          goals={goals}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </section>
          ))}
        </div>

        <section className="order-1 rounded-[18px] bg-slate-900 p-[18px] text-white shadow-[0_12px_30px_-14px_rgba(15,23,42,.55)] lg:order-2 lg:p-[22px]">
          <h2 className="text-xs font-semibold uppercase tracking-[.06em] text-slate-400">
            Lo que tienes menos lo que debes · {netWorthQ.data?.currency ?? currentCurrency()}
          </h2>
          <p className="mt-1 text-[34px] font-bold leading-none tracking-tight tabular-nums">
            {formatMoney(net, netWorthQ.data?.currency)}
          </p>
          <dl className="mt-3 space-y-1 text-xs text-slate-300">
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">Dinero en cuentas</dt>
              <dd className="tabular-nums">
                {formatMoney(accountAssets, netWorthQ.data?.currency)}
              </dd>
            </div>
            {receivables.map((receivable) => (
              <div
                key={receivable.label}
                className="flex items-center justify-between pl-3 text-[11px] text-emerald-300"
              >
                <dt className="truncate">+ Por cobrar: {receivable.label}</dt>
                <dd className="tabular-nums">
                  {formatMoney(receivable.total, netWorthQ.data?.currency)}
                </dd>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-white/10 pt-1">
              <dt className="text-slate-400">Total que tienes</dt>
              <dd className="tabular-nums">{formatMoney(assets, netWorthQ.data?.currency)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">− Total que debes</dt>
              <dd className="tabular-nums">{formatMoney(liabilities, netWorthQ.data?.currency)}</dd>
            </div>
            {debts.map((d) => (
              <div
                key={d.label}
                className="flex items-center justify-between pl-3 text-[11px] text-slate-400"
              >
                <dt className="truncate">{d.label}</dt>
                <dd className="tabular-nums">{formatMoney(d.total, netWorthQ.data?.currency)}</dd>
              </div>
            ))}
          </dl>
          {donutData.length > 0 && (
            <>
              <div className="mt-4 flex h-3 overflow-hidden rounded-md lg:hidden">
                {donutData.map((item) => (
                  <span
                    key={item.name}
                    style={{
                      width: `${accountAssets ? (item.value / accountAssets) * 100 : 0}%`,
                      backgroundColor: item.color,
                    }}
                  />
                ))}
              </div>
              <div
                className="mx-auto my-5 hidden h-[168px] w-[168px] rounded-full p-8 lg:block"
                style={{ background: wealthGradient }}
              >
                <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-slate-900 text-center">
                  <span className="text-xs text-slate-400">{accounts.length} cuentas</span>
                  <span className="text-[15px] font-bold">{currentCurrency()}</span>
                </div>
              </div>
              <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 lg:mt-0 lg:grid-cols-1 lg:gap-y-2">
                {donutData.map((item) => (
                  <li key={item.name} className="flex min-w-0 items-center gap-2 text-xs">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="min-w-0 flex-1 truncate text-slate-300">{item.name}</span>
                    <span className="font-semibold tabular-nums text-white">
                      {formatMoney(item.value, netWorthQ.data?.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      <Modal
        open={newAccountOpen}
        title={accounts.length === 0 ? 'Crea tu primera cuenta' : 'Nueva cuenta'}
        onClose={closeNewAccount}
      >
        <NewAccountForm onDone={closeNewAccount} isFirstAccount={accounts.length === 0} />
      </Modal>
    </div>
  );
}

function AccountCard({
  account,
  accounts,
  goals,
}: {
  account: AccountWithBuckets;
  accounts: AccountWithBuckets[];
  goals: SavingsGoalView[];
}) {
  const del = useDeleteAccount();
  const [addBucket, setAddBucket] = useState(false);
  const [editAccount, setEditAccount] = useState(false);
  const [reconcileAccount, setReconcileAccount] = useState(false);
  const [reconciliationBusy, setReconciliationBusy] = useState(false);
  const [reconciliationDraft, setReconciliationDraft] = useState<ReconciliationDraft | null>(null);
  const [transactionPreset, setTransactionPreset] =
    useState<QuickAddTransactionInitialValues | null>(null);
  const [transferPreset, setTransferPreset] = useState<TransferFormInitialValues | null>(null);
  const [moveMoneyOpen, setMoveMoneyOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState<Bucket | null>(null);
  const [deletingBucket, setDeletingBucket] = useState<Bucket | null>(null);
  const delBucket = useDeleteBucket(account.id);
  const bucketTotal = account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0);
  const freeBalance = account.balance - bucketTotal;
  const goalNames = new Map(goals.map((goal) => [goal.id, goal.name]));

  return (
    <div className="min-w-0 overflow-hidden rounded-[18px] border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_28px_-18px_rgba(15,23,42,.22)] lg:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <p className="min-w-0 truncate text-base font-semibold text-slate-900 lg:text-[17px]">
              {account.name}
            </p>
            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
              {account.currency}
            </span>
          </div>
          {account.institution && (
            <p className="mt-0.5 truncate text-xs text-slate-600">{account.institution}</p>
          )}
        </div>
        <div className="sm:text-right">
          <p className="text-xs font-medium text-slate-600">Saldo total</p>
          <p className="text-xl font-bold tracking-tight text-slate-900 tabular-nums lg:text-[22px]">
            {formatMoney(account.balance, account.currency)}
          </p>
          <div className="mt-2 flex items-center justify-end gap-1">
            <Tooltip
              content="Compara el saldo real con la app y registra correctamente cualquier diferencia"
              align="end"
            >
              <button
                onClick={() => {
                  setReconciliationDraft(null);
                  setReconcileAccount(true);
                }}
                className="tap-target rounded-lg border border-slate-200 px-3 text-xs font-semibold text-savings"
              >
                Conciliar saldo
              </button>
            </Tooltip>
            <ItemActionsMenu
              itemLabel={account.name}
              title="Opciones de la cuenta"
              actions={[
                ...(account.buckets.length > 0
                  ? [
                      {
                        label: 'Mover dinero',
                        description: 'Traspasa dinero disponible hacia o desde un apartado.',
                        onSelect: () => setMoveMoneyOpen(true),
                      },
                    ]
                  : []),
                {
                  label: 'Editar datos',
                  description: 'Cambia el nombre, institución y preferencias de la cuenta.',
                  onSelect: () => setEditAccount(true),
                },
                {
                  label: 'Eliminar cuenta',
                  description: 'Sólo se podrá eliminar si no tiene historial ni vínculos.',
                  tone: 'danger' as const,
                  onSelect: () => setDeleteAccountOpen(true),
                },
              ]}
            />
          </div>
        </div>
      </div>

      {account.buckets.length > 0 && (
        <div className="mt-4 grid grid-cols-2 overflow-hidden rounded-xl border border-slate-100 bg-slate-50/70">
          <div className="min-w-0 px-3 py-2.5 sm:px-4">
            <p className="text-[10px] font-bold uppercase tracking-[.08em] text-slate-600">
              Disponible
            </p>
            <p className="mt-0.5 truncate text-[15px] font-bold text-slate-900 tabular-nums">
              {formatMoney(freeBalance, account.currency)}
            </p>
            <p className="mt-0.5 text-xs leading-4 text-slate-600">Puedes gastar o apartar</p>
          </div>
          <div className="min-w-0 border-l border-slate-100 px-3 py-2.5 sm:px-4">
            <p className="text-[10px] font-bold uppercase tracking-[.08em] text-slate-600">
              En apartados
            </p>
            <p className="mt-0.5 truncate text-[15px] font-bold text-savings tabular-nums">
              {formatMoney(bucketTotal, account.currency)}
            </p>
            <p className="mt-0.5 text-xs leading-4 text-slate-600">Reservado con un propósito</p>
          </div>
        </div>
      )}

      {account.buckets.length > 0 && (
        <ul className="mt-4 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-3">
          {account.buckets.map((b) => (
            <li key={b.id}>
              <span className="mb-1.5 flex items-center justify-between gap-2 text-[13px]">
                <span className="truncate text-slate-700">{b.name}</span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="font-semibold text-slate-900 tabular-nums">
                    {formatMoney(b.balance, account.currency)}
                  </span>
                  <button
                    onClick={() => setEditingBucket(b)}
                    className="tap-target text-xs font-semibold text-savings"
                  >
                    Ajustar
                  </button>
                  <button
                    onClick={() => setDeletingBucket(b)}
                    className="tap-target text-xs text-slate-600 hover:text-expense"
                    aria-label={`Eliminar apartado ${b.name}`}
                  >
                    ×
                  </button>
                </span>
              </span>
              <span className="block h-[7px] overflow-hidden rounded bg-slate-100">
                <span
                  className="block h-full rounded bg-savings"
                  style={{
                    width: `${account.balance > 0 ? Math.min(100, (b.balance / account.balance) * 100) : 0}%`,
                  }}
                />
              </span>
              {b.goalId && (
                <span className="mt-1 block truncate text-[11px] text-savings">
                  Meta · {goalNames.get(b.goalId) ?? 'Meta vinculada'}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {[AccountType.BANK, AccountType.SAVINGS, AccountType.CETES, AccountType.CAJA_AHORRO].includes(
        account.type,
      ) && (
        <button
          onClick={() => setAddBucket(true)}
          className="mt-4 flex min-h-11 w-full items-center justify-center rounded-xl border border-dashed border-slate-300 text-[13px] font-semibold text-savings transition hover:border-savings hover:bg-slate-50"
        >
          <span className="mr-1 text-lg font-normal">+</span> Apartado
        </button>
      )}

      <Modal
        open={addBucket}
        title={`Apartado en ${account.name}`}
        onClose={() => setAddBucket(false)}
      >
        <NewBucketForm account={account} goals={goals} onDone={() => setAddBucket(false)} />
      </Modal>
      <Modal
        open={editAccount}
        title={`Editar datos de ${account.name}`}
        onClose={() => setEditAccount(false)}
      >
        <AccountDetailsForm account={account} onDone={() => setEditAccount(false)} />
      </Modal>
      <Modal
        open={reconcileAccount}
        title={`Conciliar saldo de ${account.name}`}
        eyebrow="Saldo real vs. saldo registrado"
        size="wide"
        preventClose={reconciliationBusy}
        onClose={() => {
          setReconcileAccount(false);
          setReconciliationDraft(null);
        }}
      >
        <AccountReconciliationForm
          account={account}
          accounts={accounts}
          initialDraft={reconciliationDraft}
          onDone={() => {
            setReconcileAccount(false);
            setReconciliationDraft(null);
          }}
          onBusyChange={setReconciliationBusy}
          onStartTransaction={(preset, draft) => {
            setReconciliationDraft(draft);
            setReconcileAccount(false);
            setTransactionPreset(preset);
          }}
          onStartTransfer={(preset, draft) => {
            setReconciliationDraft(draft);
            setReconcileAccount(false);
            setTransferPreset(preset);
          }}
        />
      </Modal>
      <Modal
        open={Boolean(transactionPreset)}
        title={
          transactionPreset?.type === TransactionType.INCOME
            ? 'Registrar ingreso faltante'
            : 'Registrar gasto faltante'
        }
        eyebrow="Conciliación de saldo"
        size="wide"
        preventClose={reconciliationBusy}
        onClose={() => {
          setTransactionPreset(null);
          setReconcileAccount(true);
        }}
      >
        {transactionPreset && (
          <QuickAddTransactionForm
            initialValues={transactionPreset}
            lockAccountingFields
            onBusyChange={setReconciliationBusy}
            onDone={() => {
              setTransactionPreset(null);
              setReconciliationDraft(null);
            }}
          />
        )}
      </Modal>
      <Modal
        open={Boolean(transferPreset)}
        title="Registrar traspaso faltante"
        eyebrow="Conciliación de saldo"
        size="wide"
        preventClose={reconciliationBusy}
        onClose={() => {
          setTransferPreset(null);
          setReconcileAccount(true);
        }}
      >
        {transferPreset && (
          <TransferForm
            initialValues={transferPreset}
            lockAccountingFields
            onBusyChange={setReconciliationBusy}
            onDone={() => {
              setTransferPreset(null);
              setReconciliationDraft(null);
            }}
          />
        )}
      </Modal>
      <Modal
        open={moveMoneyOpen}
        title={`Mover dinero en ${account.name}`}
        onClose={() => setMoveMoneyOpen(false)}
      >
        <BucketBalanceTransferForm account={account} onDone={() => setMoveMoneyOpen(false)} />
      </Modal>
      <Modal
        open={Boolean(editingBucket)}
        title={editingBucket ? `Ajustar ${editingBucket.name}` : 'Ajustar apartado'}
        onClose={() => setEditingBucket(null)}
      >
        {editingBucket && (
          <BucketEditForm
            account={account}
            bucket={editingBucket}
            goals={goals}
            onDone={() => setEditingBucket(null)}
          />
        )}
      </Modal>
      <ConfirmDialog
        open={deleteAccountOpen}
        title="¿Eliminar cuenta?"
        description="Sólo puedes eliminar una cuenta sin movimientos, pagos, cobros programados ni conciliaciones de saldo. Esto evita que tu historial quede incompleto. Para dejar de usarla, primero mueve o pausa lo que tenga programado; las conciliaciones se conservan como evidencia."
        itemLabel={account.name}
        pending={del.isPending}
        error={del.isError ? getApiErrorMessage(del.error) : undefined}
        onClose={() => setDeleteAccountOpen(false)}
        onConfirm={() => del.mutate(account.id, { onSuccess: () => setDeleteAccountOpen(false) })}
      />
      <ConfirmDialog
        open={Boolean(deletingBucket)}
        title="¿Eliminar apartado?"
        description="Sólo puedes eliminar un apartado que no tenga movimientos ni cobros programados. Esto evita perder información de tus registros."
        itemLabel={deletingBucket?.name}
        pending={delBucket.isPending}
        error={delBucket.isError ? getApiErrorMessage(delBucket.error) : undefined}
        onClose={() => setDeletingBucket(null)}
        onConfirm={() => {
          if (!deletingBucket) return;
          delBucket.mutate(deletingBucket.id, { onSuccess: () => setDeletingBucket(null) });
        }}
      />
    </div>
  );
}

/**
 * Un apartado es una reserva dentro de la misma cuenta, no una segunda
 * cuenta ni un gasto. Este flujo usa un traspaso con ambas puntas en la
 * cuenta actual para conservar la trazabilidad sin modificar el saldo total.
 */
function BucketBalanceTransferForm({
  account,
  onDone,
}: {
  account: AccountWithBuckets;
  onDone: () => void;
}) {
  const create = useCreateTransfer();
  const bucketTotal = account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0);
  const availableBalance = account.balance - bucketTotal;
  const [direction, setDirection] = useState<'reserve' | 'release'>('reserve');
  const [bucketId, setBucketId] = useState(account.buckets[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(toDateInputValue);
  const [note, setNote] = useState('');

  const bucket = account.buckets.find((candidate) => candidate.id === bucketId);
  const sourceBalance = direction === 'reserve' ? availableBalance : (bucket?.balance ?? 0);
  const numericAmount = Number(amount);
  const hasAmount = amount.trim() !== '';
  const amountIsValid =
    Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount <= sourceBalance;
  const amountTooLarge =
    hasAmount && Number.isFinite(numericAmount) && numericAmount > sourceBalance;
  const actionLabel = direction === 'reserve' ? 'Apartar dinero' : 'Liberar dinero';
  const sourceLabel = direction === 'reserve' ? 'Disponible' : (bucket?.name ?? 'Apartado');
  const availableTarget = { accountId: account.id };
  const bucketTarget = bucket ? { accountId: account.id, bucketId: bucket.id } : null;
  const fromCoordinates = direction === 'reserve' ? availableTarget : bucketTarget;
  const toCoordinates = direction === 'reserve' ? bucketTarget : availableTarget;
  const fromTarget = fromCoordinates ? resolveBalanceTarget([account], fromCoordinates) : null;
  const toTarget = toCoordinates ? resolveBalanceTarget([account], toCoordinates) : null;
  const balanceChanges =
    fromCoordinates && toCoordinates && amountIsValid
      ? transferBalanceChanges([account], fromCoordinates, toCoordinates, numericAmount)
      : [];
  const automaticNote =
    direction === 'reserve'
      ? `Apartado: ${bucket?.name ?? 'reserva'}`
      : `Liberado de apartado: ${bucket?.name ?? 'reserva'}`;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!bucket || !amountIsValid) return;
    create.mutate(
      {
        fromAccountId: account.id,
        fromBucketId: direction === 'release' ? bucket.id : undefined,
        toAccountId: account.id,
        toBucketId: direction === 'reserve' ? bucket.id : undefined,
        amount: numericAmount,
        date,
        note: note.trim() || automaticNote,
      },
      { onSuccess: onDone },
    );
  };

  if (account.buckets.length === 0) {
    return (
      <p className="rounded-xl bg-amber-50 px-3 py-3 text-sm leading-5 text-amber-800">
        Crea un apartado primero para poder reservar dinero dentro de esta cuenta.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-3.5 text-sm leading-5 text-slate-700">
        <p className="font-semibold text-slate-900">Tu saldo total no cambia.</p>
        <p className="mt-0.5 text-xs text-slate-600">
          Sólo redistribuyes el dinero entre lo que puedes usar ahora y lo que quieres reservar.
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-blue-100 pt-3 text-xs">
          <div>
            <dt className="text-slate-500">Disponible hoy</dt>
            <dd className="mt-0.5 font-bold text-slate-900 tabular-nums">
              {formatMoney(availableBalance, account.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">En apartados</dt>
            <dd className="mt-0.5 font-bold text-savings tabular-nums">
              {formatMoney(bucketTotal, account.currency)}
            </dd>
          </div>
        </dl>
      </div>

      <fieldset>
        <legend className="mb-1.5 text-sm font-medium text-slate-700">¿Qué quieres hacer?</legend>
        <div
          className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1"
          role="group"
          aria-label="Dirección del dinero"
        >
          <button
            type="button"
            data-form-value-change={direction === 'reserve' ? undefined : 'true'}
            onClick={() => setDirection('reserve')}
            className={`tap-target rounded-lg px-2 text-sm font-semibold transition ${direction === 'reserve' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            aria-pressed={direction === 'reserve'}
          >
            Apartar
          </button>
          <button
            type="button"
            data-form-value-change={direction === 'release' ? undefined : 'true'}
            onClick={() => setDirection('release')}
            className={`tap-target rounded-lg px-2 text-sm font-semibold transition ${direction === 'release' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            aria-pressed={direction === 'release'}
          >
            Liberar
          </button>
        </div>
        <p className="mt-1.5 text-xs leading-4 text-slate-500">
          {direction === 'reserve'
            ? 'Apartar reserva dinero para una meta o gasto futuro.'
            : 'Liberar devuelve dinero reservado a tu saldo disponible.'}
        </p>
      </fieldset>

      <label htmlFor={`bucket-transfer-${account.id}`} className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {direction === 'reserve' ? 'Apartado de destino' : 'Apartado de origen'}
        </span>
        <select
          id={`bucket-transfer-${account.id}`}
          value={bucketId}
          onChange={(event) => setBucketId(event.target.value)}
          className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
        >
          {account.buckets.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name} · {formatMoney(candidate.balance, account.currency)}
            </option>
          ))}
        </select>
      </label>

      <Field
        id={`bucket-transfer-amount-${account.id}`}
        label="Monto a mover"
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0.01"
        max={sourceBalance}
        required
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        helperText={`Máximo desde ${sourceLabel}: ${formatMoney(sourceBalance, account.currency)}.`}
      />
      {amountTooLarge && (
        <p role="alert" className="-mt-2 text-xs leading-4 text-expense">
          El monto supera lo que tienes en {sourceLabel.toLowerCase()}.
        </p>
      )}

      {balanceChanges.length > 0 && fromTarget && toTarget && (
        <TransferBalancePreview
          changes={balanceChanges}
          from={fromTarget}
          to={toTarget}
          amount={numericAmount}
        />
      )}

      <DatePicker
        id={`bucket-transfer-date-${account.id}`}
        label="Fecha"
        required
        value={date}
        onChange={setDate}
        helperText="Se guarda como traspaso; no cuenta como ingreso ni gasto."
      />
      <Field
        id={`bucket-transfer-note-${account.id}`}
        label="Nota (opcional)"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={automaticNote}
      />

      {create.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={create.isPending || !bucket || !amountIsValid}
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-[0_8px_16px_-8px_rgba(15,23,42,.55)] disabled:opacity-60"
        >
          {create.isPending ? 'Moviendo…' : actionLabel}
        </button>
      </div>
    </form>
  );
}

/** Editar la identidad de la cuenta nunca modifica su saldo. */
function AccountDetailsForm({
  account,
  onDone,
}: {
  account: AccountWithBuckets;
  onDone: () => void;
}) {
  const update = useUpdateAccount();
  const [name, setName] = useState(account.name);
  const [institution, setInstitution] = useState(account.institution ?? '');

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    update.mutate(
      {
        id: account.id,
        body: {
          name: name.trim(),
          institution: institution.trim(),
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-xl bg-blue-50 px-3 py-2.5 text-sm leading-5 text-slate-600">
        Aquí sólo cambias cómo identificas la cuenta. Para corregir cuánto dinero tiene, usa
        “Conciliar saldo”.
      </p>
      <Field
        id={`acc-edit-name-${account.id}`}
        label="Nombre para identificarla"
        required
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Ej. Cuenta donde recibo mi sueldo"
      />
      <Field
        id={`acc-edit-inst-${account.id}`}
        label="Banco o institución (opcional)"
        value={institution}
        onChange={(event) => setInstitution(event.target.value)}
        placeholder="Escribe el banco, plataforma o caja"
      />
      {update.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(update.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={update.isPending || !name.trim()}
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {update.isPending ? 'Guardando…' : 'Guardar datos'}
        </button>
      </div>
    </form>
  );
}

/**
 * Compara la fotografía de la app con una fuente externa y obliga a elegir la
 * explicación contable antes de cambiar el saldo. Así una corrección técnica
 * no se convierte por accidente en ingreso o gasto del mes.
 */
function AccountReconciliationForm({
  account,
  accounts,
  initialDraft,
  onDone,
  onBusyChange,
  onStartTransaction,
  onStartTransfer,
}: {
  account: AccountWithBuckets;
  accounts: AccountWithBuckets[];
  initialDraft: ReconciliationDraft | null;
  onDone: () => void;
  onBusyChange: (busy: boolean) => void;
  onStartTransaction: (
    preset: QuickAddTransactionInitialValues,
    draft: ReconciliationDraft,
  ) => void;
  onStartTransfer: (preset: TransferFormInitialValues, draft: ReconciliationDraft) => void;
}) {
  const create = useCreateAccountReconciliation();
  const historyQ = useAccountReconciliations(account.id);
  const undo = useUndoAccountReconciliation();
  const [balance, setBalance] = useState(initialDraft?.balance ?? String(account.balance));
  const [date, setDate] = useState(initialDraft?.date ?? reconciliationToday());
  const [resolution, setResolution] = useState<ReconciliationResolution | null>(null);
  const [reason, setReason] = useState<AccountReconciliationReason>(
    AccountReconciliationReason.BANK_RECONCILIATION,
  );
  const [note, setNote] = useState('');
  const submissionRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const operationPending = create.isPending || undo.isPending;

  useEffect(() => {
    onBusyChange(operationPending);
    return () => onBusyChange(false);
  }, [onBusyChange, operationPending]);

  const bucketTotal = account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0);
  const numericBalance = Number(balance);
  const balanceWasEntered = balance.trim() !== '';
  const balanceIsValid =
    balanceWasEntered &&
    Number.isFinite(numericBalance) &&
    numericBalance >= 0 &&
    numericBalance <= MAX_SAFE_MONEY &&
    hasAtMostTwoMoneyDecimals(numericBalance);
  const bucketsWouldExceedBalance = balanceIsValid && numericBalance < bucketTotal - 0.005;
  const today = reconciliationToday();
  const dateIsValid = isValidCalendarDate(date) && date <= today;
  const rawDelta = balanceIsValid ? numericBalance - account.balance : 0;
  const delta = Math.round((rawDelta + Number.EPSILON) * 100) / 100;
  const hasDifference = Math.abs(delta) >= 0.005;
  const isIncrease = delta > 0;
  const movementResolution: 'income' | 'expense' = isIncrease ? 'income' : 'expense';
  const availableBefore = account.balance - bucketTotal;
  const availableAfter = balanceIsValid ? numericBalance - bucketTotal : availableBefore;
  const compatibleAccounts = accounts.filter(
    (candidate) => candidate.id !== account.id && candidate.currency === account.currency,
  );
  const transferAvailable = compatibleAccounts.length > 0;
  const effectiveResolution =
    resolution === movementResolution || resolution === 'neutral' || resolution === 'transfer'
      ? resolution
      : null;
  const neutralDateIsValid = effectiveResolution !== 'neutral' || date === today;
  const canContinue =
    balanceIsValid &&
    !bucketsWouldExceedBalance &&
    dateIsValid &&
    neutralDateIsValid &&
    hasDifference &&
    Boolean(effectiveResolution) &&
    (effectiveResolution !== 'transfer' || transferAvailable);

  const realAttemptFor = (selectedResolution: Exclude<ReconciliationResolution, 'neutral'>) => {
    const fingerprint = JSON.stringify({
      accountId: account.id,
      expectedBalance: account.balance,
      confirmedBalance: numericBalance,
      date,
      resolution: selectedResolution,
    });
    const existing = initialDraft?.realAttempt;
    return existing?.fingerprint === fingerprint
      ? existing
      : {
          fingerprint,
          key: createReconciliationAttemptKey(
            selectedResolution === 'transfer' ? 'real-transfer' : 'real-transaction',
          ),
        };
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue || !effectiveResolution) return;

    if (effectiveResolution === movementResolution) {
      const realAttempt = realAttemptFor(effectiveResolution);
      onStartTransaction(
        {
          type: isIncrease ? TransactionType.INCOME : TransactionType.EXPENSE,
          amount: Math.abs(delta),
          accountId: account.id,
          date,
          reconciliation: {
            accountId: account.id,
            expectedBalance: account.balance,
            idempotencyKey: realAttempt.key,
          },
        },
        { balance, date, realAttempt },
      );
      return;
    }

    if (effectiveResolution === 'transfer') {
      const realAttempt = realAttemptFor(effectiveResolution);
      onStartTransfer(
        {
          from: isIncrease ? undefined : account.id,
          to: isIncrease ? account.id : undefined,
          amount: Math.abs(delta),
          date,
          reconciliation: {
            accountId: account.id,
            expectedBalance: account.balance,
            idempotencyKey: realAttempt.key,
          },
        },
        { balance, date, realAttempt },
      );
      return;
    }

    const request = {
      expectedBalance: account.balance,
      balance: numericBalance,
      date,
      reason,
      note: note.trim() || undefined,
    };
    const fingerprint = JSON.stringify(request);
    if (submissionRef.current?.fingerprint !== fingerprint) {
      submissionRef.current = { fingerprint, key: createReconciliationAttemptKey('neutral') };
    }
    create.mutate(
      {
        accountId: account.id,
        body: { ...request, idempotencyKey: submissionRef.current.key },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <HelperText tone="info">
        Compara el saldo total de la app con el que confirmaste para esta cuenta. Después indica qué
        explica la diferencia para no alterar tus ingresos o gastos por error.
      </HelperText>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-start">
        <div className="space-y-4">
          <Field
            id={`account-reconciliation-balance-${account.id}`}
            label="Saldo total que tiene esta cuenta hoy"
            type="number"
            inputMode="decimal"
            step="0.01"
            min={bucketTotal}
            max={MAX_SAFE_MONEY}
            required
            autoFocus
            disabled={operationPending}
            value={balance}
            onChange={(event) => setBalance(event.target.value)}
            helperText={
              account.buckets.length > 0
                ? `Incluye los ${formatMoney(bucketTotal, account.currency)} que ya separaste en apartados.`
                : 'Escribe lo que ves en tu banco o institución, o lo que contaste si es efectivo.'
            }
          />
          {balanceWasEntered && !balanceIsValid && (
            <HelperText tone="danger" role="alert">
              Escribe un saldo entre $0 y $90 billones, con un máximo de dos decimales.
            </HelperText>
          )}
          {bucketsWouldExceedBalance && (
            <HelperText tone="danger" role="alert">
              {account.buckets.length > 0
                ? `El saldo no puede ser menor que los ${formatMoney(bucketTotal, account.currency)} que ya tienes en apartados. Primero libera dinero de esos apartados.`
                : 'El saldo total no puede ser negativo.'}
            </HelperText>
          )}
          <DatePicker
            id={`account-reconciliation-date-${account.id}`}
            label="Fecha del ajuste"
            value={date}
            onChange={setDate}
            disabled={operationPending}
            min={effectiveResolution === 'neutral' ? today : undefined}
            max={today}
            required
            helperText={
              effectiveResolution === 'neutral'
                ? 'El ajuste neutral usa hoy porque estás comparando el saldo actual. Si falta un movimiento real, elige su fecha original.'
                : 'Si falta un ingreso, gasto o traspaso real, elige la fecha en que ocurrió. El importe de arriba siempre debe ser el saldo actual.'
            }
          />
          {!dateIsValid && (
            <HelperText tone="danger" role="alert">
              Elige una fecha válida de hoy o anterior.
            </HelperText>
          )}
          {dateIsValid && !neutralDateIsValid && (
            <div role="alert">
              <HelperText tone="danger">
                Un ajuste neutral debe llevar la fecha de hoy. Para una fecha anterior, registra el
                ingreso, gasto o traspaso que ocurrió ese día.
              </HelperText>
            </div>
          )}

          {balanceIsValid && !bucketsWouldExceedBalance && (
            <section
              aria-live="polite"
              className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5"
            >
              <h3 className="text-sm font-bold text-slate-900">Vista previa</h3>
              <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <div className="rounded-xl bg-white p-3">
                  <dt className="text-[11px] font-bold uppercase tracking-[.06em] text-slate-500">
                    En la app
                  </dt>
                  <dd className="mt-1 font-bold tabular-nums text-slate-900">
                    {formatMoney(account.balance, account.currency)}
                  </dd>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <dt className="text-[11px] font-bold uppercase tracking-[.06em] text-slate-500">
                    Saldo confirmado
                  </dt>
                  <dd className="mt-1 font-bold tabular-nums text-slate-900">
                    {formatMoney(numericBalance, account.currency)}
                  </dd>
                </div>
                <div className="col-span-2 rounded-xl bg-white p-3 sm:col-span-1">
                  <dt className="text-[11px] font-bold uppercase tracking-[.06em] text-slate-500">
                    Diferencia
                  </dt>
                  <dd
                    className={`mt-1 font-bold tabular-nums ${hasDifference ? (isIncrease ? 'text-income' : 'text-expense') : 'text-slate-700'}`}
                  >
                    {signedMoney(delta, account.currency)}
                  </dd>
                </div>
              </dl>
              {account.buckets.length > 0 && (
                <p className="mt-3 text-xs leading-5 text-slate-600">
                  Dinero disponible después: {formatMoney(availableBefore, account.currency)} →{' '}
                  <strong className="text-slate-900">
                    {formatMoney(availableAfter, account.currency)}
                  </strong>
                  . Tus apartados no cambian.
                </p>
              )}
              {!hasDifference && (
                <p className="mt-3 text-xs font-semibold text-emerald-700">
                  El saldo ya coincide. No necesitas registrar ningún cambio.
                </p>
              )}
            </section>
          )}
        </div>

        <div className="space-y-4">
          {hasDifference && balanceIsValid && !bucketsWouldExceedBalance && (
            <fieldset>
              <legend className="text-sm font-bold text-slate-900">
                ¿Qué explica la diferencia de {formatMoney(Math.abs(delta), account.currency)}?
              </legend>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Elige la causa real. Cada opción cambia tus reportes de una forma distinta.
              </p>
              <div className="mt-3 space-y-2">
                <ReconciliationChoice
                  name={`account-reconciliation-resolution-${account.id}`}
                  value={movementResolution}
                  checked={effectiveResolution === movementResolution}
                  onChange={setResolution}
                  disabled={operationPending}
                  title={isIncrease ? 'Registrar ingreso faltante' : 'Registrar gasto faltante'}
                  description={
                    isIncrease
                      ? 'Sí recibiste dinero y el movimiento no está registrado. Cuenta como ingreso del mes.'
                      : 'Sí pagaste algo y el movimiento no está registrado. Cuenta como gasto del mes.'
                  }
                />
                <ReconciliationChoice
                  name={`account-reconciliation-resolution-${account.id}`}
                  value="transfer"
                  checked={effectiveResolution === 'transfer'}
                  onChange={setResolution}
                  disabled={operationPending || !transferAvailable}
                  title={isIncrease ? 'Registrar traspaso recibido' : 'Registrar traspaso enviado'}
                  description={
                    transferAvailable
                      ? 'El dinero cambió entre dos cuentas tuyas. No cuenta como ingreso ni gasto.'
                      : `Necesitas otra cuenta en ${account.currency} para registrar el traspaso.`
                  }
                />
                <ReconciliationChoice
                  name={`account-reconciliation-resolution-${account.id}`}
                  value="neutral"
                  checked={effectiveResolution === 'neutral'}
                  onChange={(value) => {
                    setResolution(value);
                    setDate(today);
                  }}
                  disabled={operationPending}
                  title="Ajustar sólo el saldo"
                  description="Corrige la cuenta y deja evidencia de la conciliación, sin crear ingreso, gasto ni traspaso."
                />
              </div>
            </fieldset>
          )}

          {effectiveResolution === 'neutral' && hasDifference && (
            <div className="space-y-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
              <HelperText tone="info">
                El ajuste neutral cambia el saldo y tu patrimonio, pero no se suma a ingresos,
                gastos ni presupuesto. Úsalo cuando no falte un movimiento real.
              </HelperText>
              <label htmlFor={`account-reconciliation-reason-${account.id}`} className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Motivo del ajuste
                </span>
                <select
                  id={`account-reconciliation-reason-${account.id}`}
                  value={reason}
                  disabled={operationPending}
                  onChange={(event) => setReason(event.target.value as AccountReconciliationReason)}
                  className="tap-target h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                >
                  {RECONCILIATION_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor={`account-reconciliation-note-${account.id}`} className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Nota (opcional)
                </span>
                <textarea
                  id={`account-reconciliation-note-${account.id}`}
                  value={note}
                  disabled={operationPending}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  maxLength={240}
                  placeholder="Ej. Saldo confirmado en la app de mi institución"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Te ayudará a reconocer esta corrección en el historial.
                </span>
              </label>
            </div>
          )}
        </div>
      </div>

      <AccountReconciliationHistory
        account={account}
        reconciliations={historyQ.data ?? []}
        loading={historyQ.isLoading}
        error={historyQ.isError ? getApiErrorMessage(historyQ.error) : undefined}
        retry={() => void historyQ.refetch()}
        undoPending={operationPending}
        undoError={undo.isError ? getApiErrorMessage(undo.error) : undefined}
        onUndo={(reconciliationId) =>
          undo.mutate({ accountId: account.id, reconciliationId }, { onSuccess: onDone })
        }
      />

      {create.isError && (
        <div role="alert" aria-live="assertive">
          <HelperText tone="danger">{getApiErrorMessage(create.error)}</HelperText>
        </div>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={!canContinue || operationPending}
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-[0_8px_16px_-8px_rgba(15,23,42,.55)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {create.isPending
            ? 'Guardando ajuste…'
            : undo.isPending
              ? 'Deshaciendo ajuste…'
              : !hasDifference
                ? 'No hay diferencia por conciliar'
                : effectiveResolution === 'neutral'
                  ? 'Ajustar saldo sin movimiento'
                  : effectiveResolution === 'transfer'
                    ? 'Continuar al traspaso'
                    : effectiveResolution === movementResolution
                      ? `Continuar al ${isIncrease ? 'ingreso' : 'gasto'}`
                      : 'Elige cómo resolver la diferencia'}
        </button>
      </div>
    </form>
  );
}

function ReconciliationChoice({
  name,
  value,
  checked,
  disabled = false,
  title,
  description,
  onChange,
}: {
  name: string;
  value: ReconciliationResolution;
  checked: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  onChange: (value: ReconciliationResolution) => void;
}) {
  return (
    <label
      className={`flex gap-3 rounded-xl border p-3 transition ${
        checked
          ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
          : disabled
            ? 'cursor-not-allowed border-slate-100 bg-slate-50/70 opacity-65'
            : 'cursor-pointer border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        className="mt-1 h-4 w-4 shrink-0 accent-slate-900"
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-5 text-slate-600">{description}</span>
      </span>
    </label>
  );
}

function AccountReconciliationHistory({
  account,
  reconciliations,
  loading,
  error,
  retry,
  undoPending,
  undoError,
  onUndo,
}: {
  account: AccountWithBuckets;
  reconciliations: AccountReconciliation[];
  loading: boolean;
  error?: string;
  retry: () => void;
  undoPending: boolean;
  undoError?: string;
  onUndo: (reconciliationId: string) => void;
}) {
  return (
    <details className="group rounded-2xl border border-slate-200 bg-white">
      <summary className="tap-target flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-800 marker:hidden">
        <span>
          Historial de conciliaciones
          <span className="ml-2 text-xs font-normal text-slate-500">
            {loading
              ? 'Cargando…'
              : error
                ? 'No se pudo cargar'
                : `${reconciliations.length} registros`}
          </span>
        </span>
        <span className="text-slate-500 transition group-open:rotate-180" aria-hidden>
          ⌄
        </span>
      </summary>
      <div className="border-t border-slate-100 px-4 py-3">
        {loading && <Skeleton className="block h-20 w-full" />}
        {error && (
          <div className="space-y-2" role="alert" aria-live="assertive">
            <HelperText tone="danger">{error}</HelperText>
            <Button type="button" variant="secondary" onClick={retry}>
              Volver a intentar
            </Button>
          </div>
        )}
        {!loading && !error && reconciliations.length === 0 && (
          <p className="text-sm leading-5 text-slate-600">
            Aún no has hecho ajustes neutrales en esta cuenta.
          </p>
        )}
        {!loading && !error && reconciliations.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {reconciliations.map((item) => {
              const active = item.status === AccountReconciliationStatus.ACTIVE;
              return (
                <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {RECONCILIATION_REASON_LABEL.get(item.reason) ?? 'Conciliación'}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {formatCalendarDate(item.date)} ·{' '}
                        {active ? 'Ajuste activo' : 'Ajuste deshecho'}
                      </p>
                    </div>
                    <p
                      className={`text-sm font-bold tabular-nums ${item.delta >= 0 ? 'text-income' : 'text-expense'}`}
                    >
                      {signedMoney(item.delta, account.currency)}
                    </p>
                  </div>
                  <p className="mt-1.5 text-xs text-slate-600">
                    {formatMoney(item.previousBalance, account.currency)} →{' '}
                    {formatMoney(item.newBalance, account.currency)}
                  </p>
                  {item.note && <p className="mt-1 text-xs text-slate-600">{item.note}</p>}
                  {active && (
                    <button
                      type="button"
                      disabled={undoPending}
                      onClick={() => onUndo(item.id)}
                      className="tap-target mt-2 text-xs font-semibold text-expense disabled:opacity-60"
                    >
                      {undoPending ? 'Deshaciendo…' : 'Deshacer este ajuste'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {undoError && (
          <div role="alert" aria-live="assertive">
            <HelperText tone="danger">{undoError}</HelperText>
          </div>
        )}
      </div>
    </details>
  );
}

function BucketEditForm({
  account,
  bucket,
  goals,
  onDone,
}: {
  account: AccountWithBuckets;
  bucket: Bucket;
  goals: SavingsGoalView[];
  onDone: () => void;
}) {
  const update = useUpdateBucket(account.id);
  const [name, setName] = useState(bucket.name);
  const [balance, setBalance] = useState(String(bucket.balance));
  const [goalId, setGoalId] = useState(bucket.goalId ?? '');

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    update.mutate(
      {
        bucketId: bucket.id,
        body: {
          name: name.trim(),
          balance: Number(balance),
          // `null` es intencional: permite desvincular la meta al elegir
          // “Sin meta”, sin dejar el apartado apuntando a una meta anterior.
          goalId: goalId || null,
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-sm leading-5 text-slate-600">
        Un apartado es dinero que separas dentro de esta misma cuenta. No es otra cuenta ni un
        gasto.
      </p>
      <Field
        id={`bucket-edit-name-${bucket.id}`}
        label="Nombre"
        required
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Field
        id={`bucket-edit-balance-${bucket.id}`}
        label="Monto reservado"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        required
        value={balance}
        onChange={(event) => setBalance(event.target.value)}
        helperText={`No puede superar el saldo de ${account.name}.`}
      />
      <GoalLinkField
        id={`bucket-edit-goal-${bucket.id}`}
        value={goalId}
        onChange={setGoalId}
        goals={goals}
        helperText="Si cambias la meta, este dinero dejará de contar en la meta anterior y contará en la nueva."
      />
      {update.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(update.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={update.isPending}
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {update.isPending ? 'Guardando…' : 'Guardar apartado'}
        </button>
      </div>
    </form>
  );
}

function NewAccountForm({
  onDone,
  isFirstAccount,
}: {
  onDone: () => void;
  isFirstAccount: boolean;
}) {
  const create = useCreateAccount();
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>(AccountType.BANK);
  const [balance, setBalance] = useState('0');
  const [institution, setInstitution] = useState('');
  const [currency, setCurrency] = useState(currentCurrency());

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        name: name.trim(),
        type,
        currency,
        balance: Number(balance) || 0,
        institution: type === AccountType.CASH ? undefined : institution.trim() || undefined,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {isFirstAccount && (
        <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-600">
          Registra cuánto dinero tienes hoy. Si después creas apartados, el saldo total incluirá
          también ese dinero separado.
        </p>
      )}
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          ¿Dónde está el dinero?
        </span>
        <select
          className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          value={type}
          onChange={(e) => setType(e.target.value as AccountType)}
        >
          {ACCOUNT_TYPE_ORDER.map((t) => (
            <option key={t} value={t}>
              {ACCOUNT_TYPE_META[t].label}
            </option>
          ))}
        </select>
        <span className="mt-1.5 block text-xs leading-4 text-slate-500">
          Elige banco, ahorro, inversión, caja o efectivo.
        </span>
      </label>
      <Field
        id="acc-name"
        label="Nombre para identificarla"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ej. Cuenta donde recibo mi sueldo"
      />
      <Field
        id="acc-balance"
        label="Saldo total de hoy"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        required
        value={balance}
        onChange={(e) => setBalance(e.target.value)}
        helperText="Es todo el dinero que tienes en esta cuenta. Si el banco separa dinero en apartados, inclúyelo aquí."
      />
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Moneda</span>
        <select
          className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          value={currency}
          onChange={(e) => setCurrency(e.target.value as typeof currency)}
        >
          <option value="MXN">Pesos mexicanos (MXN)</option>
          <option value="USD">Dólares estadounidenses (USD)</option>
          <option value="CAD">Dólares canadienses (CAD)</option>
        </select>
      </label>
      {type !== AccountType.CASH && (
        <Field
          id="acc-inst"
          label="Banco o institución (opcional)"
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          placeholder="Escribe el banco, plataforma o caja"
          helperText="Puedes dejarlo vacío si no quieres registrarlo."
        />
      )}
      {create.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {create.isPending ? 'Guardando…' : 'Crear cuenta'}
        </button>
      </div>
    </form>
  );
}

function GoalLinkField({
  id,
  value,
  onChange,
  goals,
  helperText,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  goals: SavingsGoalView[];
  helperText: string;
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        Meta relacionada (opcional)
      </span>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base text-slate-900"
      >
        <option value="">No relacionar con una meta</option>
        {goals.map((goal) => (
          <option key={goal.id} value={goal.id}>
            {goal.name}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs leading-5 text-slate-500">{helperText}</span>
    </label>
  );
}

function NewBucketForm({
  account,
  goals,
  onDone,
}: {
  account: AccountWithBuckets;
  goals: SavingsGoalView[];
  onDone: () => void;
}) {
  const create = useCreateBucket(account.id);
  const [name, setName] = useState('');
  const [balance, setBalance] = useState('0');
  const [goalId, setGoalId] = useState('');
  const [addToAccountBalance, setAddToAccountBalance] = useState(false);
  const submissionRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const currentReserved = account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0);
  const enteredAmount = Number(balance);
  const amount = Number.isFinite(enteredAmount) && enteredAmount >= 0 ? enteredAmount : 0;
  const nextTotal = account.balance + (addToAccountBalance ? amount : 0);
  const nextReserved = currentReserved + amount;
  const nextAvailable = nextTotal - nextReserved;
  const amountIsValid =
    balance.trim() !== '' && Number.isFinite(enteredAmount) && enteredAmount >= 0;
  const exceedsAvailable = amountIsValid && nextAvailable < -0.005;
  const canSubmit = Boolean(name.trim()) && amountIsValid && !exceedsAvailable;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const request = {
      name: name.trim(),
      balance: amount,
      goalId: goalId || undefined,
      addToAccountBalance: addToAccountBalance && amount > 0,
    };
    const fingerprint = JSON.stringify(request);
    if (submissionRef.current?.fingerprint !== fingerprint) {
      submissionRef.current = { fingerprint, key: createBucketAttemptKey() };
    }
    create.mutate(
      {
        ...request,
        idempotencyKey: submissionRef.current.key,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-xl bg-blue-50 px-3 py-2.5 text-sm leading-5 text-slate-700">
        Un apartado es dinero que separas dentro de esta misma cuenta. Te ayuda a no gastarlo por
        accidente; no es otra cuenta ni un gasto.
      </p>
      <Field
        id="bk-name"
        label="¿Para qué quieres separar este dinero?"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ej. Renta, emergencia o vacaciones"
      />
      <Field
        id="bk-balance"
        label="¿Cuánto dinero ya tienes separado?"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        required
        value={balance}
        onChange={(e) => setBalance(e.target.value)}
        helperText="Puedes escribir 0 si sólo quieres crear el apartado y agregar dinero después."
      />
      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3.5">
        <input
          type="checkbox"
          checked={addToAccountBalance}
          onChange={(event) => setAddToAccountBalance(event.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-slate-900"
        />
        <span>
          <span className="block text-sm font-semibold leading-5 text-slate-900">
            Este dinero todavía no está incluido en el saldo total
          </span>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            Márcalo si el dinero ya existe en tu cuenta, pero no lo sumaste al saldo que
            registraste. La app hará la suma por ti.
          </span>
        </span>
      </label>

      {exceedsAvailable && (
        <p className="rounded-xl bg-red-50 px-3 py-2.5 text-sm leading-5 text-expense" role="alert">
          No hay suficiente dinero disponible para este apartado. Si este dinero sí existe en la
          cuenta y todavía no lo sumaste, marca la opción de arriba.
        </p>
      )}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5" aria-live="polite">
        <p className="text-sm font-semibold text-slate-900">Así quedará tu cuenta</p>
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-slate-600">Saldo total</dt>
            <dd className="font-semibold tabular-nums text-slate-900">
              {formatMoney(nextTotal, account.currency)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-slate-600">En apartados</dt>
            <dd className="font-semibold tabular-nums text-savings">
              {formatMoney(nextReserved, account.currency)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-2">
            <dt className="font-medium text-slate-700">Disponible para usar</dt>
            <dd
              className={`font-bold tabular-nums ${nextAvailable < -0.005 ? 'text-expense' : 'text-slate-900'}`}
            >
              {formatMoney(nextAvailable, account.currency)}
            </dd>
          </div>
        </dl>
        <p className="mt-2 text-xs leading-4 text-slate-500">
          Saldo total = dinero disponible + dinero en apartados.
        </p>
      </div>
      <GoalLinkField
        id="bk-goal"
        value={goalId}
        onChange={setGoalId}
        goals={goals}
        helperText="Si eliges una meta, este dinero contará como parte de lo que ya guardaste para cumplirla."
      />
      {create.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={create.isPending || !canSubmit}
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {create.isPending ? 'Guardando…' : 'Crear apartado'}
        </button>
      </div>
    </form>
  );
}
